import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Modal from "./UI/Modal";

const News = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const openDialog = (content) => {
    setDialogContent(content);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setDialogContent(null);
  };

  const dialogContents = {
    launch: {
      title: "Project Launch Announcement",
      content: (
        <div className="space-y-4 sm:space-y-6">
          <div className="text-center mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2">
              Launching the DCA Project: Building Bridges of Inclusion from{" "}
              <strong>Cairo, Egypt</strong> 🇪🇬
            </h3>
          </div>

          <div className="prose prose-sm sm:prose-base md:prose-lg max-w-none text-gray-700 leading-relaxed text-sm sm:text-base">
            <p>
              From <strong>April 22 to 26, 2025</strong>, representatives from
              seven partner organizations across Europe and the Mediterranean
              gathered in <strong>Cairo, Egypt</strong>, to officially launch
              the "Differences – Challenge – Assumptions (DCA)" project. Hosted
              by <strong>Have a Dream</strong>, this kickoff meeting marked the
              beginning of a three-year collaborative journey aimed at
              challenging negative narratives, promoting intersectional
              understanding, and fostering inclusive youth work practices.
            </p>

            <p>
              During the meeting, partners from <strong>Portugal</strong>,{" "}
              <strong>Romania</strong>, <strong>Poland</strong>,{" "}
              <strong>Jordan</strong>, <strong>Tunisia</strong>,{" "}
              <strong>Morocco</strong>, and <strong>Egypt</strong> came together
              to align on the project's vision, objectives, and long-term
              impact. The sessions focused on defining work packages, timelines,
              and responsibilities—ensuring a shared understanding of the
              project's flow and deliverables.
            </p>

            <p>
              The DCA project aspires to challenge dominant social paradigms and
              create a network of 21 youth experts capable of addressing
              negative narratives related to diversity and inclusion in their
              local contexts. Throughout its implementation, the project will
              also reach nearly 5,000 young people across seven countries
              through both offline workshops and digital campaigns designed to
              increase sensitivity toward diversity.
            </p>

            <p>
              The kickoff meeting was filled with productive discussions,
              interactive team-building activities, and moments of cultural
              connection. Participants shared their expectations and reflected
              on how cooperation and collective learning can inspire real
              societal change. The consortium also explored tools for
              transparency, sustainability, and communication, emphasizing
              collaboration and trust as the foundation of their work.
            </p>

            <p>
              Hosted in the vibrant city of <strong>Cairo</strong>, the launch
              served not only as an administrative milestone but also as a
              symbolic one—uniting partners under the shared belief that
              diversity is a strength and that transforming narratives begins
              with dialogue, empathy, and action.
            </p>

            <p>
              The DCA consortium now looks ahead to the next milestone: the
              first training course on Diversity, Intersectionality, and
              Narratives, set to take place in <strong>Bușteni, Romania</strong>
              , in <strong>August 2025</strong>.
            </p>
          </div>
        </div>
      ),
    },
    midterm: {
      title: "Mid-term Meeting Highlights",
      content: (
        <div className="space-y-4 sm:space-y-6">
          <div className="text-center mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2">
              Mid-term Meeting Highlights —{" "}
              <strong>Tiznit, Morocco</strong>
            </h3>
          </div>

          <div className="prose prose-sm sm:prose-base md:prose-lg max-w-none text-gray-700 leading-relaxed text-sm sm:text-base">
            <p>
              The Differences – Challenge – Assumptions (DCA) project reached an
              important milestone with its mid-term meeting, hosted by{" "}
              <strong>Morocco's Friends Foundation</strong> in the vibrant city
              of <strong>Tiznit, Morocco</strong>, on January 30–31, 2026.
              Representatives from all partner organizations —{" "}
              <strong>Teatro Metaphora (Portugal)</strong>,{" "}
              <strong>A.R.T. Fusion (Romania)</strong>,{" "}
              <strong>We Add Wings (Poland)</strong>,{" "}
              <strong>EuroMed EVE (Tunisia)</strong>,{" "}
              <strong>Jordan Youth Innovation Forum (Jordan)</strong>,{" "}
              <strong>Morocco's Friends Foundation (Morocco)</strong>, and{" "}
              <strong>Have a Dream (Egypt)</strong> — gathered to take stock of
              the first year of the project and chart a clear course for the
              months ahead.
            </p>

            <p>
              Over two intensive days of review, debate, and collective
              decision-making, the partners:
            </p>

            <ul className="space-y-2 sm:space-y-3 ml-4 sm:ml-6">
              <li className="flex items-start">
                <span className="text-emerald-600 mr-3 mt-1">•</span>
                <span>Reflected on year one with honesty and depth</span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 mr-3 mt-1">•</span>
                <span>Advanced the research and toolkit work</span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 mr-3 mt-1">•</span>
                <span>
                  Prepared the ground for the second Training Course in Tunisia
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 mr-3 mt-1">•</span>
                <span>Shaped the local action phase</span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 mr-3 mt-1">•</span>
                <span>Streamlined communication and dissemination</span>
              </li>
            </ul>

            <p>
              Beyond the working sessions, the meeting was enriched by moments
              of genuine connection.
            </p>

            <p>
              This mid-term meeting reaffirmed what makes the DCA consortium
              tick: a group of organizations from across Europe, the Middle
              East, and North Africa, united by a shared belief that diversity
              is a strength — and that the narratives surrounding it can and
              must change. With the second Training Course in Tunisia on the
              horizon and local actions about to begin in all seven countries,
              the project is entering its most visible and impactful phase yet.
            </p>
          </div>
        </div>
      ),
    },
    kickoff: {
      title: "Kickoff Meeting Highlights",
      content: (
        <div className="space-y-4 sm:space-y-6">
          <div className="text-center mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2">
              Kickoff Meeting Highlights — <strong>Cairo, Egypt</strong> (April
              22–26, 2025)
            </h3>
          </div>

          <div className="prose prose-sm sm:prose-base md:prose-lg max-w-none text-gray-700 leading-relaxed text-sm sm:text-base">
            <p>
              The Differences – Challenge – Assumptions (DCA) project officially
              began with a dynamic kickoff meeting in{" "}
              <strong>Cairo, Egypt</strong>, hosted by{" "}
              <strong>Have a Dream</strong>. Representatives from all partner
              organizations — <strong>Teatro Metaphora (Portugal)</strong>,{" "}
              <strong>A.R.T. Fusion (Romania)</strong>,{" "}
              <strong>We Add Wings (Poland)</strong>,{" "}
              <strong>EuroMed EVE (Tunisia)</strong>,{" "}
              <strong>Jordan Youth Innovation Forum (Jordan)</strong>,{" "}
              <strong>Morocco's Friends Foundation (Morocco)</strong>, and{" "}
              <strong>Have a Dream (Egypt)</strong> — gathered to co-create the
              foundation of this ambitious initiative.
            </p>

            <p>
              Over three days of collaboration, creativity, and dialogue, the
              partners:
            </p>

            <ul className="space-y-2 sm:space-y-3 ml-4 sm:ml-6">
              <li className="flex items-start">
                <span className="text-emerald-600 mr-3 mt-1">•</span>
                <span>
                  Aligned on the project vision and objectives, reaffirming
                  their shared mission to challenge negative narratives around
                  diversity and promote inclusive thinking.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 mr-3 mt-1">•</span>
                <span>
                  Reviewed the project's structure and work packages, clarifying
                  roles, responsibilities, and expected outcomes across six key
                  components: coordination, research and toolkit development,
                  training, local actions, monitoring and evaluation, and
                  communication.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 mr-3 mt-1">•</span>
                <span>
                  Agreed on the project's timeline, with key milestones such as
                  the first training course in{" "}
                  <strong>Bușteni, Romania (August 2025)</strong>, the second in{" "}
                  <strong>Tunisia (early 2026)</strong>, and a mid-term meeting
                  in <strong>Morocco</strong>.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 mr-3 mt-1">•</span>
                <span>
                  Established strong communication and transparency tools,
                  including a shared project drive, evaluation mechanisms, and
                  monthly consortium meetings to ensure smooth cooperation.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 mr-3 mt-1">•</span>
                <span>
                  Explored sustainability and ethical standards, adopting
                  eco-conscious travel and inclusivity practices, and agreeing
                  on a shared code of conduct for all activities.
                </span>
              </li>
            </ul>

            <p>
              Beyond the formal sessions, the meeting fostered trust and
              connection among partners through team-building exercises and
              intercultural exchanges — from sharing expectations to discovering
              the vibrant culture of <strong>Cairo</strong>.
            </p>

            <p>
              This meeting set a solid foundation for the DCA project's next
              stages, uniting all partners around a common purpose: to redefine
              societal narratives, elevate diversity awareness, and empower
              youth to become changemakers in their communities.
            </p>
          </div>
        </div>
      ),
    },
  };

  const categories = [
    { id: "all", name: "All Posts", color: "from-gray-500 to-gray-600" },
    {
      id: "projectUpdates",
      name: t("news.categories.projectUpdates"),
      color: "from-emerald-500 to-teal-500",
    },
    {
      id: "youthVoices",
      name: t("news.categories.youthVoices"),
      color: "from-teal-500 to-cyan-500",
    },
    {
      id: "eventRecap",
      name: t("news.categories.eventRecap"),
      color: "from-emerald-600 to-teal-600",
    },
    {
      id: "awarenessCampaigns",
      name: t("news.categories.awarenessCampaigns"),
      color: "from-teal-600 to-emerald-600",
    },
  ];

  const posts = [
    {
      id: "launch",
      category: "projectUpdates",
      title: t("news.posts.launch.title"),
      description: t("news.posts.launch.description"),
      action: t("news.posts.launch.action"),
      isDisabled: false,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      ),
    },
    {
      id: "youthPerspectives",
      category: "youthVoices",
      title: t("news.posts.youthPerspectives.title"),
      description: t("news.posts.youthPerspectives.description"),
      action: t("news.posts.youthPerspectives.action"),
      isDisabled: true,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
    },
    {
      id: "kickoff",
      category: "eventRecap",
      title: t("news.posts.kickoff.title"),
      description: t("news.posts.kickoff.description"),
      action: t("news.posts.kickoff.action"),
      isDisabled: false,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      id: "midterm",
      category: "eventRecap",
      title: "Mid-term Meeting Highlights",
      description:
        "A summary of the key discussions and decisions made during the project's transnational partners meeting with all the consortium.",
      action: "Read more",
      isDisabled: false,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      id: "campaign",
      category: "awarenessCampaigns",
      title: t("news.posts.campaign.title"),
      description: t("news.posts.campaign.description"),
      action: t("news.posts.campaign.action"),
      isDisabled: true,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
          />
        </svg>
      ),
    },
  ];

  const filteredPosts =
    selectedCategory === "all"
      ? posts
      : posts.filter((post) => post.category === selectedCategory);

  return (
    <section
      ref={sectionRef}
      id="news"
      className="py-20 relative overflow-hidden"
      style={{ backgroundColor: "var(--section-background)" }}
    >
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-green-50/30 via-transparent to-blue-50/30"></div>
        <div className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-r from-green-200/20 to-emerald-200/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-r from-blue-200/20 to-indigo-200/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 md:px-8">
        {/* Section header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t("news.title")}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto rounded-full mb-8"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay updated with the latest news, stories, and insights from our
            community
          </p>
        </div>

        {/* Notice banner */}
        <div
          className={`mb-16 transition-all duration-1000 delay-300 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-400 rounded-2xl p-6 shadow-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-yellow-800 font-medium text-lg">
                  {t("news.notice")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Category filter */}
        <div
          className={`mb-12 transition-all duration-1000 delay-500 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  selectedCategory === category.id
                    ? `bg-gradient-to-r ${category.color} text-white shadow-lg scale-105`
                    : "bg-white text-gray-600 hover:bg-gray-50 hover:scale-105 shadow-md"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Posts grid */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-8 transition-all duration-1000 delay-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {filteredPosts.map((post, index) => {
            const categoryInfo = categories.find(
              (cat) => cat.id === post.category
            );
            return (
              <div
                key={post.id}
                className={`group relative bg-white rounded-3xl p-8 shadow-lg border border-gray-100 transition-all duration-300 flex flex-col h-full ${
                  post.isDisabled
                    ? "cursor-not-allowed opacity-75"
                    : "hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-2"
                }`}
              >
                {/* Category badge */}
                <div
                  className={`w-fit items-center px-4 py-2 rounded-full text-sm font-semibold mb-4 bg-gradient-to-r ${categoryInfo?.color} text-white`}
                >
                  {categoryInfo?.name}
                </div>

                {/* Post content */}
                <div className="flex items-start mb-6 flex-grow">
                  <div
                    className={`w-12 h-12 bg-gradient-to-r ${categoryInfo?.color} rounded-xl flex items-center justify-center text-white mr-4 group-hover:scale-110 transition-transform duration-300`}
                  >
                    {post.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 group-hover:text-gray-800 transition-colors duration-300 mb-3">
                      {post.title}
                    </h3>
                    <p className="text-lg leading-relaxed text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                      {post.description}
                    </p>
                  </div>
                </div>

                {/* Action button at bottom */}
                <div className="flex items-center justify-between mt-auto">
                  <button
                    disabled={post.isDisabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (post.id === "launch") {
                        openDialog(dialogContents.launch);
                      } else if (post.id === "kickoff") {
                        openDialog(dialogContents.kickoff);
                      } else if (post.id === "midterm") {
                        openDialog(dialogContents.midterm);
                      }
                    }}
                    className={`inline-flex items-center font-semibold transition-all duration-300 group/btn px-4 py-2 rounded-lg ${
                      post.isDisabled
                        ? "text-gray-400 cursor-not-allowed"
                        : `hover:bg-gray-50 ${
                            categoryInfo?.id === "projectUpdates"
                              ? "text-emerald-600 hover:text-emerald-700"
                              : categoryInfo?.id === "youthVoices"
                              ? "text-teal-600 hover:text-teal-700"
                              : categoryInfo?.id === "eventRecap"
                              ? "text-emerald-600 hover:text-emerald-700"
                              : "text-teal-600 hover:text-teal-700"
                          }`
                    }`}
                  >
                    <span className="mr-2">{post.action}</span>
                    {!post.isDisabled && (
                      <svg
                        className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    )}
                  </button>
                </div>

                {/* Hover overlay */}
                {post.isDisabled && (
                  <div
                    className={`absolute inset-0 bg-gradient-to-r ${categoryInfo?.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300 rounded-3xl`}
                  ></div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Dialog Modal */}
      <Modal
        isOpen={isDialogOpen}
        onClose={closeDialog}
        title={dialogContent?.title}
        size="xl"
      >
        {dialogContent?.content}
      </Modal>
    </section>
  );
};

export default News;
